import { MouseEvent, useCallback, useMemo, useState } from 'react';
import { Stack, Typography } from '@mui/material';

import {
  Button,
  ExpandableTableRow,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@occc-app/design-system';
import { StatusIndicator } from '@occc/ui-design-system';
import { ScanDataWeeklySubmissionStatus } from '@occc/graphql';

import { TobaccoWeeklySubmissionsTableRowDetails } from './tobacco-weekly-submissions-table-row-details.component';

import {
  formatInt,
  formatSubmittedAt,
  formatUsdCents,
  getScanDataSubmissionStatusParams,
} from '../tobacco-submissions.utils';
import { RetailerNetworkScanDataWeeklySubmission } from '../tobacco-submissions.types';

const FIRST_COLUMN_WIDTH = 12; // spacing units
const SECOND_COLUMN_WIDTH = 18; // spacing units

type TobaccoWeeklySubmissionsTableRowProps = {
  item: RetailerNetworkScanDataWeeklySubmission;
  onViewAllStoresClick: (submission: RetailerNetworkScanDataWeeklySubmission) => void;
};

export const TobaccoWeeklySubmissionsTableRow = ({
  item,
  onViewAllStoresClick,
}: TobaccoWeeklySubmissionsTableRowProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const toggle = useCallback(() => setIsExpanded((v) => !v), []);

  const estimatedReimbursement =
    item.estimatedReimbursements.multiUnit + item.estimatedReimbursements.loyalty;

  const cellStyle = useMemo(
    () => (isExpanded ? { borderBottom: 'none' } : undefined),
    [isExpanded],
  );

  const statusParams = useMemo(() => getScanDataSubmissionStatusParams(item.status), [item.status]);

  const isViewAllButtonVisible =
    item.totalSitesSubmitted > 0 && item.status !== ScanDataWeeklySubmissionStatus.NotSubmitted;
  const onViewAllStores = useCallback(
    (event: MouseEvent<unknown>) => {
      event.stopPropagation();
      onViewAllStoresClick(item);
    },
    [item, onViewAllStoresClick],
  );

  const expandedContent = (
    <>
      <TableCell />
      <TableCell colSpan={4} sx={{ px: '0!important', py: 0 }}>
        <Table
          sx={{
            '& th:first-of-type, & td:first-of-type': {
              paddingLeft: (theme) => theme.spacing(2),
            },
          }}
        >
          <TableHead>
            <TableRow hover={false}>
              <TableCell sx={{ width: (theme) => theme.spacing(FIRST_COLUMN_WIDTH) }}>
                Manufacturer
              </TableCell>
              <TableCell sx={{ width: (theme) => theme.spacing(SECOND_COLUMN_WIDTH) }}>
                Submission Status
              </TableCell>
              <TableCell>Multi Unit Transactions</TableCell>
              <TableCell>Loyalty Transactions</TableCell>
              <TableCell align='right'>Estimated Reimbursement</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!item.submissions.length && (
              <TableRow hover={false}>
                <TableCell colSpan={5}>
                  <Typography>No submissions available for this week.</Typography>
                </TableCell>
              </TableRow>
            )}
            {item.submissions.map((submission, index) => (
              <TobaccoWeeklySubmissionsTableRowDetails key={index} submission={submission} />
            ))}
          </TableBody>
        </Table>
      </TableCell>
    </>
  );

  return (
    <ExpandableTableRow
      isExpanded={isExpanded}
      onClick={toggle}
      expandedContent={expandedContent}
      arrowCellSx={isExpanded ? cellStyle : undefined}
    >
      <TableCell sx={{ ...cellStyle, width: (theme) => theme.spacing(FIRST_COLUMN_WIDTH) }}>
        <Typography variant='body2'>{formatSubmittedAt(item.submittedAt)}</Typography>
      </TableCell>

      <TableCell sx={{ ...cellStyle, width: (theme) => theme.spacing(SECOND_COLUMN_WIDTH) }}>
        <StatusIndicator color={statusParams.color} label={statusParams.label} />
      </TableCell>

      <TableCell sx={cellStyle}>
        <Stack direction='row' alignItems='center' gap={1}>
          <Typography variant='body2'>{formatInt(item.totalSitesSubmitted)}</Typography>
          {isViewAllButtonVisible ? (
            <Button
              variant='text'
              size='small'
              sx={{ textTransform: 'none' }}
              onClick={onViewAllStores}
            >
              View All
            </Button>
          ) : null}
        </Stack>
      </TableCell>

      <TableCell sx={cellStyle} align='right'>
        <Typography variant='body2'>{formatUsdCents(estimatedReimbursement)}</Typography>
      </TableCell>
    </ExpandableTableRow>
  );
};
